




















































































































































































































import Vue from "vue";
import { default as moment } from "moment-timezone";
import { forkJoin, timer } from "rxjs";
import { take, takeWhile } from "rxjs/operators";
import {
  DraggableEvent,
  DraggableContainer,
  DraggableRow,
} from "@/directives/dragging";

import {
  CalendarDay,
  CalendarDayGroup,
  CalendarDayGroups,
  Summe,
  MonatsabschlussZeile,
} from "@/api/Abrechnung";

import { createDameMonatsblattExport } from "@/api/Reporting";
import { Dame, MONATSNAMEN, TIMEZONE } from "@/api/Termine";
import { isComputer } from "@/service/DeviceType";

import { default as MonatsabschlussEditContextMenu, MenuItemSelected, PopoverMode } from "./MonatsabschlussEditContextMenu.vue";
import { default as MonatsabschlussEditPopover, MonatsabschlussUpdateEvent } from "./MonatsabschlussEditPopover.vue";
import { default as Unterschrift } from "./Unterschrift.vue";

declare type DayBuffer = { [tag: string]: CalendarDay };

function getTableRowTag(tags: HTMLElement[]): HTMLElement | null {
  const maxIterations = Math.min(5, tags.length);
  for (let i = 0; i < maxIterations; i++) {
    if (tags[i] && tags[i].tagName.toUpperCase() == "TR") {
      return tags[i];
    }
  }
  return null;
}

function hashCode(s: string) {
  let h = 0,
    i = s.length - 1;
  while (i >= 0) {
    h = ((h << 5) - h + s.charCodeAt(--i)) | 0;
  }
  return h;
}

export default Vue.extend({
  name: "DameMonatsblatt",
  components: {
    Unterschrift,
    MonatsabschlussEditContextMenu,
    MonatsabschlussEditPopover,
  },
  directives: {
    "draggable-container": DraggableContainer,
    "draggable-row": DraggableRow,
  },
  props: {
    studio: {
      type: Object,
      validator: (studio: any) =>
        studio == null || (studio.id && studio.bezeichner),
    },
    monat: { type: Number, required: true },
    jahr: { type: Number, required: true },
    damenDesMonats: {
      type: Array,
      required: true,
    },
    dameIndex: Number,
  },
  data() {
    return {
      draggableOptions: {
        draggingClass: "dragging",
        highlightClass: "highlight",
        onDragStart: "dragStart",
        onDragOver: "dragOver",
        onDragEnd: "dragEnd",
      },
      dame: {} as Dame,
      calendarDayGroups: [] as CalendarDayGroups,
      summe: {
        summe: 0,
        summeDdSteuer: 0,
      } as Summe,
      monatsdaten: [] as MonatsabschlussZeile[],
      monatsdatenZeile: {} as MonatsabschlussZeile,
      draggingEnabled: false,
      dragSourceTag: "0",
      dragTargetTag: "0",
      lastEvent: "",
      selectedDays: {} as DayBuffer,
      dameZurueckDisabled: false,
      dameVorDisabled: false,
      tageZusammenfuehrenDialogSichtbar: false,
    };
  },
  mounted(): void {
    this.updateAllData();
  },
  computed: {
    monatLesbar(): string {
      if (this.monat >= 0 && this.monat <= 12) {
        return `${MONATSNAMEN[this.monat]} / ${this.jahr}`;
      } else {
        return "<unbestimmt>";
      }
    },
    draggingEnabledOnDevice(): boolean {
      return this.draggingEnabled && isComputer();
    },
  },
  watch: {
    damenDesMonats(damenDesMonats: Dame[]): void {
      this.updateAllData();
    },
    dameIndex(newDameIndex: number): void {
      this.updateAllData();
    },
  },
  methods: {
    format(betrag: number, printZeros = false): string {
      return betrag > 0 || printZeros
        ? betrag.toLocaleString("de-DE", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })
        : "";
    },
    setDame(offset: number): void {
      const neueDameIndex = this.dameIndex + offset;
      this.dameZurueckDisabled = neueDameIndex <= 0;
      this.dameVorDisabled = neueDameIndex >= this.damenDesMonats.length - 1;
      if (neueDameIndex < 0 && neueDameIndex >= this.damenDesMonats.length) {
        this.$toast.add({
          severity: "error",
          summary: "Auswahl ungültig",
          detail: "Die gewählte Dame ist nicht verfügbar",
          life: 3000,
        });
        return;
      }

      this.$emit("dameSelected", neueDameIndex);
    },
    dragStart(event: any): void {
      const tr = getTableRowTag(event.originalEvent.path);
      if (!tr) {
        return;
      }
      this.dragSourceTag = (tr as any).dataset.tag;
    },
    dragOver(event: any): void {
      const tr = getTableRowTag(event.originalEvent.path);
      if (!tr) {
        return;
      }
      this.dragTargetTag = (tr as any).dataset.tag;
    },
    dragEnd(event: any): void {
      this.$store.dispatch("appLoading", true);
      this.tageseinnahmenVerschieben(
        this.dragSourceTag,
        this.dragTargetTag
      ).then(() => {
        this.$store.dispatch("appLoading", false);
      });
    },
    getRowCssClass(tag: CalendarDay) {
      return this.isDaySelected(tag.tag) ? "highlight" : "";
    },
    getCssClass(betrag: number) {
      return betrag ? "spalte-betrag hat-preise" : "spalte-betrag";
    },
    formatDatum(tag: string): string {
      return `${tag}.${(this.monat < 9 ? "0" : "") + (this.monat + 1)}.${this.jahr}`;
    },
    updateSignature(day: CalendarDay, wer: string, imageDataURL: string) {
      const prop =
        "unterschrift" + wer.substring(0, 1).toUpperCase() + wer.substring(1);
      console.log("Neue Unterschrift für Tag " + day.buchungstag + "; " + prop);
      (day as any)[prop] = imageDataURL;
      const monat = moment(new Date(this.jahr, this.monat));
      this.$znaApi.buchungstagSpeichern(this.studio, monat, this.dame, day);
      //this.$znaApi.buchungstagUnterschrift(this.studio, monat, day.tag, wer, imageDataURL);
      this.$forceUpdate();
    },
    isDaySelected(tag: string): boolean {
      return !!this.selectedDays[tag];
    },
    onDayClicked(tag: CalendarDay) {
      if (this.draggingEnabled && !isComputer()) {
        if (this.isDaySelected(tag.tag)) {
          tag.renderKey = `tag-${tag.tag}`;
          delete this.selectedDays[tag.tag];
        } else {
          tag.renderKey = `tag-${tag.tag}-selected`;
          this.selectedDays[tag.tag] = tag;
        }
        (this.$refs
          .dameMonatsblattZusammenfuehrenDialog as any).updateOptions();
        this.tageZusammenfuehrenDialogSichtbar =
          Object.keys(this.selectedDays).length > 1;
        this.lastEvent =
          "Sichtbar: " +
          this.tageZusammenfuehrenDialogSichtbar +
          "; tage: " +
          Object.keys(this.selectedDays).join(",");
      }
    },
    changeMode() {
      if (!this.draggingEnabled) {
        this.tageZusammenfuehrenDialogSichtbar = false;
        this.selectedDays = {};
      }
    },
    async targetDaySelected(targetDay: CalendarDay) {
      this.lastEvent = "Ziel-Tag: " + targetDay.tag;
      this.$store.dispatch("appLoading", true);
      const targetDayNumber = parseInt(targetDay.tag);

      for (const tagAsString in this.selectedDays) {
        const vonTag = parseInt(tagAsString);
        if (vonTag == targetDayNumber) {
          continue;
        }
        await this.tageseinnahmenVerschieben(tagAsString, targetDay.tag);
      }

      this.$store.dispatch("appLoading", false);
      this.tageZusammenfuehrenDialogSichtbar = false;
      this.selectedDays = {};
    },
    clearDataTable(): void {
      this.calendarDayGroups = [];
      const monat = moment(new Date(this.jahr, this.monat));
      const maxDay = monat.daysInMonth();

      let wochenIndex = 1;
      const sevenDays: CalendarDay[] = [];
      for (let tag = 1; tag <= maxDay; tag++) {
        const tagAsFormattedString = (tag < 10 ? "0" : "") + tag;
        sevenDays.push({
          tag: tagAsFormattedString,
          buchungstag: tagAsFormattedString,
          anzahlTermine: 0,
          miete: 0,
          steuer: 0,
          renderKey: `tag-${tag}`,
        });
      }
      while (sevenDays.length > 0) {
        const groups = this.calendarDayGroups;
        if (!groups) {
          console.log("calendarGroups undefiniert!");
          break;
        }
        groups.push({
          wochenIndex: wochenIndex++,
          days: sevenDays.splice(0, Math.min(7, sevenDays.length)),
          summe: 0,
          summeDdSteuer: 0,
          anzahlTage: 0,
        });
      }
    },
    async tageseinnahmenVerschieben(vonTag: string, nachTag: string) {
      const vonTagAsNumber = parseInt(vonTag);
      const nachTagAsNumber = parseInt(nachTag);
      if (
        vonTagAsNumber <= 0 ||
        vonTagAsNumber >= 32 ||
        nachTagAsNumber <= 0 ||
        nachTagAsNumber >= 32 ||
        vonTagAsNumber == nachTagAsNumber
      ) {
        console.log("Keine gültigen Tage ausgewählt; nix wird verschoben.");
        return;
      } else {
        //this.dragSourceTag = this.dragTargetTag = 0;
      }
      console.log(`Verschiebe ${vonTag} auf ${nachTag}...`);
      const sourceTag = this.getTageszeile(vonTag);
      const targetTag = this.getTageszeile(nachTag);
      const monat = moment(new Date(this.jahr, this.monat));
      const dame = this.damenDesMonats[this.dameIndex] as Dame;
      this.$znaApi
        .buchungstagVerschieben(
          this.studio,
          monat,
          dame,
          sourceTag.tag,
          targetTag.tag,
          false
        )
        .subscribe((monatsdaten: MonatsabschlussZeile[] | string) => {
          if (Array.isArray(monatsdaten)) {
            this.neueMonatsdaten(monatsdaten);
          }
        });
    },
    updateAllData(): void {
      if (this.dameIndex < 0 || this.dameIndex >= this.damenDesMonats.length) {
        this.dame = {} as Dame;
        this.clearDataTable();
        return;
      }
      this.retrieveData();
    },
    retrieveData(): void {
      if (this.dameIndex >= this.damenDesMonats.length) {
        console.log(
          "Datenaktualisierung verhindert, da keine gültige Dame ausgewählt wurde"
        );
        return;
      }
      this.dame = this.damenDesMonats[this.dameIndex] as Dame;

      this.$store.dispatch("appLoading", true);

      const monat = moment(new Date(this.jahr, this.monat));
      this.$znaApi
        .getTermineMonat(this.studio, monat, false, this.dame as Dame)
        .subscribe(
          (monatsdaten) => {
            timer(200)
              .pipe(
                takeWhile(
                  (value, index) => this.calendarDayGroups == undefined
                ),
                take(100)
              )
              .subscribe(
                () => {
                  // Hier wird nur gepollt
                },
                () => {
                  // Kann bei `timer` nicht auftreten
                  this.$store.dispatch("appLoading", false);
                },
                () => {
                  // calendarGroups ist/sind nun definiert
                  this.neueMonatsdaten(monatsdaten);

                  this.$store.dispatch("appLoading", false);
                }
              );
          },
          (error: any) => {
            this.$toast.add({
              severity: "error",
              summary: "Laden der Daten fehlgeschlagen",
              detail: error,
              life: 3000,
            });
            this.$store.dispatch("appLoading", false);
          }
        );
    },
    neueMonatsdaten(monatsdaten: MonatsabschlussZeile[]): void {
      this.clearDataTable();
      this.monatsdaten = monatsdaten;

      monatsdaten
        .flatMap((md) => md.tage || [])
        .forEach((abrechnungTag) => {
          const buchungstag = this.getTageszeile(abrechnungTag.buchungstag);
          buchungstag.miete += abrechnungTag.miete + (abrechnungTag.manuelleAnpassung || 0);
          buchungstag.steuer += abrechnungTag.steuer;
          buchungstag.anzahlTermine += abrechnungTag.anzahlTermine;

          const terminTag = this.getTageszeile(abrechnungTag.tag);
          terminTag.unterschriftDame = abrechnungTag.unterschriftDame;
          terminTag.unterschriftStudio = abrechnungTag.unterschriftStudio;

          if (terminTag.miete > 0) {
            terminTag.unterschriftStudio = "data:image/png;base64,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";
          }
        });

      this.updateSummen();
      this.monatsdatenZeile = monatsdaten[0];
    },
    getTageszeile(tag: string): CalendarDay {
      const buchungstag = parseInt(tag);
      const wochengruppenIndex = Math.floor((buchungstag - 1) / 7);
      const selectedDayGroup = this.calendarDayGroups[
        wochengruppenIndex
      ] as CalendarDayGroup;
      const selectedDay = selectedDayGroup.days[
        Math.floor((buchungstag - 1) % 7)
      ] as CalendarDay;
      return selectedDay;
    },
    updateSummen(): void {
      const gesamtsumme: Summe = { summe: 0, summeDdSteuer: 0, anzahlTage: 0 };
      this.calendarDayGroups.forEach((calendarDayGroup: CalendarDayGroup) => {
        calendarDayGroup.anzahlTage = 0;
        calendarDayGroup.days.forEach((day) => {
          calendarDayGroup.summe += day.miete;
          calendarDayGroup.summeDdSteuer += day.steuer;
          if (day.tag == day.buchungstag) {
            calendarDayGroup.anzahlTage++;
          }
        });
        gesamtsumme.summe += calendarDayGroup.summe;
        gesamtsumme.summeDdSteuer += calendarDayGroup.summeDdSteuer;
        gesamtsumme.anzahlTage += calendarDayGroup.anzahlTage;
      });
      const summe: Summe = this.summe;
      summe.summe = gesamtsumme.summe;
      summe.summeDdSteuer = gesamtsumme.summeDdSteuer;
    },
    monatsabschlussNeuBerechnen() {
      const monat = moment(new Date(this.jahr, this.monat, 1, 12));
      this.$store.dispatch("appLoading", true);
      this.$znaApi.monatsabschlussNeuBerechnen(this.studio, monat, false, this.dame)
        .subscribe(monatsdaten => {
            this.neueMonatsdaten(monatsdaten);
            this.$store.dispatch("appLoading", false);
        });
    },
    openContextMenu(kalendertag: string) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        if (kalendertag) {
          const contextMenu = this.$refs.contextMenu as any;
          console.log(kalendertag);
          contextMenu.open(kalendertag, moment().year(this.jahr).month(this.monat).daysInMonth(), this.dame.id, this.getTageszeile(kalendertag).miete);
        }
      }
    },
    editOrCreate(kalendertag: string) {
      console.log("editOrCreate");
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        if (kalendertag) {
          this.showDialog("edit", kalendertag, this.dame.id || 0, this.getTageszeile(kalendertag).miete);
        }
      }
    },
    calcNachTag(vonTag: string, deltaTag: number): string {
      const nachTagNum = parseInt(vonTag) + deltaTag;
      return ((nachTagNum < 10) ? "0" : "") + nachTagNum;
    },
    onMenuItemSelected(selectionEvent: MenuItemSelected) {
      switch (selectionEvent.popoverMode) {
        case "edit":
        case "select-date":
          this.showDialog(
            selectionEvent.popoverMode,
            selectionEvent.kalendertag,
            selectionEvent.dame,
            selectionEvent.miete
          );
          break;
        case "move-previous":
          this.tageseinnahmenVerschieben(selectionEvent.kalendertag, this.calcNachTag(selectionEvent.kalendertag, -1));
          break;
        case "move-next":
          this.tageseinnahmenVerschieben(selectionEvent.kalendertag, this.calcNachTag(selectionEvent.kalendertag, 1));
          break;
      }
    },
    showDialog(popoverMode: PopoverMode, tag: string, dame: number, miete: number) {
      console.debug(event);
      const popover: any = this.$refs.popover;
      const monat = moment().tz(TIMEZONE).year(this.jahr).month(this.monat);
      this.$nextTick(() => popover.show(popoverMode, tag, monat.daysInMonth(), monat.format('.MM.YYYY'), dame, miete));
    },
    monatsabschlussUpdated(update: MonatsabschlussUpdateEvent) {
      const tagWurdeGeaendert = update.tag != parseInt(update.tagOriginal);

      switch (update.popoverMode) {
        case "select-date":
          if (tagWurdeGeaendert) {
            // Tag wurde tatsächlich verschoben (Preis war nicht geändert worden)
            this.tageseinnahmenVerschieben(update.tagOriginal, this.calcNachTag(""+update.tag, 0));
          }
          break;

        case "edit":
          console.log('Edit: ');
          console.log(update);
          this.$store.dispatch("appLoading", true);
          this.$znaApi.updateMonatsabschlussDame(
            this.studio,
            moment().tz(TIMEZONE).year(this.jahr).month(this.monat).date(update.tag),
            update.dame,
            update.miete
          ).subscribe(_ => {
            this.retrieveData();
          });
          break;
      }
    },
    exportPdf() {
      const monat = moment(new Date(this.jahr, this.monat));
      createDameMonatsblattExport(
        this.monatsdatenZeile,
        this.studio,
        monat
      ).then((pdfDocument) => {
        console.log("Monatsblatt als PDF erzeugt");

        // Siehe https://pdfmake.github.io/docs/getting-started/client-side/methods/
        // pdfDocument.download();
        pdfDocument.open();
        /*
        pdfDocument.getDataUrl((dataUrl) => {
          const targetElement = document.querySelector("#iframeContainer");
          const iframe = document.createElement("iframe");
          iframe.src = dataUrl;
          targetElement.appendChild(iframe);
        });
        */
      });
    },
  },
});
